import { useState, type FormEvent } from 'react';
import { navLinks } from '@/mocks/content';
import LiquidGlass from '@/components/feature/LiquidGlass';

const FORM_URL = 'https://readdy.ai/api/form/db6pn3v891islr12udc0';

type Status = 'idle' | 'sending' | 'success' | 'error';

const inputCls = 'mt-2 w-full bg-background-50/40 border-b border-foreground-950/25 hover:border-foreground-950/50 focus:border-accent-600 focus:bg-background-50/70 focus-visible:outline-none px-2 py-3 text-[16px] text-foreground-950 placeholder:text-foreground-500 transition-colors';
const labelCls = 't-label text-foreground-700';

export default function Contact() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');
  const [count, setCount] = useState(0);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const trap = String(fd.get('website_alt') || '').trim();
    if (trap) { setStatus('success'); return; }
    fd.delete('website_alt');
    const name = String(fd.get('name') || '').trim();
    const email = String(fd.get('email') || '').trim();
    const message = String(fd.get('message') || '').trim();
    if (!name || !email || !message) { setFormError('Please fill in your name, email and message.'); setStatus('error'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) { setFormError('Please enter a valid email address.'); setStatus('error'); return; }
    if (message.length > 500) { setFormError('Message must be 500 characters or fewer.'); setStatus('error'); return; }
    const params = new URLSearchParams();
    fd.forEach((v, k) => { if (typeof v === 'string') params.append(k, v); });
    setStatus('sending');
    setFormError('');
    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: unknown } } | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const detail = typeof parsed?.meta?.detail === 'string' ? parsed.meta.detail : '';
      const serverMsg = parsed?.meta?.message || parsed?.message || detail || responseText;
      const isSpam = /spam/i.test(serverMsg || '');
      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
        setCount(0);
      } else {
        setFormError(serverMsg || 'Your message could not be sent. Please try again.');
        setStatus('error');
      }
    } catch {
      setFormError('Network error. Please check your connection and try again.');
      setStatus('error');
    }
  };

  return (
    <>
      <section id="contact" aria-label="Contact" className="relative text-foreground-950">
        <LiquidGlass>
        <div className="mgrid rev">
          <div className="half-r cell cell-y flex flex-col justify-between gap-14">
            <p className="t-label text-foreground-700 flex items-center gap-3"><span className="veil text-accent-700">06</span><span className="h-px w-6 bg-foreground-950/20" /><span className="veil">Contact</span></p>
            <div className="reveal">
              <h2 className="t-hero max-w-[640px]">Let&apos;s make your business easier to find.</h2>
              <p className="veil mt-8 t-body text-foreground-900 max-w-[440px]">Tell us about your business and where you want to be found. We&apos;ll reply with an honest view of where to start.</p>
              <a href="#contact-form" className="group mt-8 inline-flex items-center gap-2 text-[17px] min-h-[44px] cursor-pointer">
                <span className="veil border-b border-accent-600 group-hover:text-accent-700">Start a conversation</span>
                <span aria-hidden="true">↗</span>
              </a>
            </div>
          </div>
          <div className="half-l cell cell-y flex flex-col justify-end lg:row-start-1 lg:col-start-1">
            <p className="veil self-start t-label text-foreground-700 mb-10">Project enquiry — replies within two working days</p>
            <form id="contact-form" data-readdy-form onSubmit={onSubmit} className="relative flex flex-col gap-8 max-w-[560px]" noValidate>
              <div className="rd-aux-wrap">
                <label htmlFor="website_alt">Alternate website</label>
                <input id="website_alt" type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly onFocus={(ev) => ev.currentTarget.removeAttribute('readonly')} />
              </div>
              <label className="flex flex-col"><span className={labelCls}>Name</span>
                <input name="name" required className={inputCls} placeholder="Your name" autoComplete="name" />
              </label>
              <label className="flex flex-col"><span className={labelCls}>Work email</span>
                <input name="email" type="email" required className={inputCls} placeholder="you@business.com" autoComplete="email" />
              </label>
              <label className="flex flex-col"><span className={labelCls}>Website (optional)</span>
                <input name="website" type="url" className={inputCls} placeholder="https://" />
              </label>
              <label className="flex flex-col"><span className={labelCls}>Message</span>
                <textarea name="message" required maxLength={500} rows={4} className={`${inputCls} resize-none`} placeholder="What would you like to improve?" onChange={(ev) => setCount(ev.target.value.length)} />
                <span className="t-label text-foreground-600 mt-2 self-end">{count}/500</span>
              </label>
              <button type="submit" disabled={status === 'sending'} className="h-12 px-6 bg-accent-600 text-background-50 text-[15px] font-medium hover:bg-accent-700 transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-500 disabled:opacity-60 cursor-pointer whitespace-nowrap self-start rounded-[2px]">
                {status === 'sending' ? 'Sending…' : 'Send message ↗'}
              </button>
              <div aria-live="polite" className="min-h-[24px] text-[14px]">
                {status === 'success' && <p className="text-accent-700">Thanks — your message has been received. We&apos;ll be in touch.</p>}
                {status === 'error' && <p className="text-foreground-900"><i className="ri-error-warning-line mr-1.5 text-accent-700" />{formError}</p>}
              </div>
            </form>
          </div>
        </div>
        </LiquidGlass>
      </section>
      <footer className="bg-primary-500 text-background-50">
        <div className="mgrid dark !border-t-0">
          <div className="half-l cell">
            <p className="font-heading text-[32px] md:text-[40px] font-medium tracking-[-0.04em] leading-none">RADIUS</p>
            <p className="mt-3 text-[14px] text-background-300">Local growth, made visible.</p>
          </div>
          <nav aria-label="Footer" className="half-r cell flex flex-wrap items-end gap-x-8 gap-y-2">
            {navLinks.map((l) => (
              <a key={l.href} href={l.href} className="text-[14px] text-background-200 hover:text-accent-400 min-h-[44px] flex items-center cursor-pointer whitespace-nowrap">{l.label}</a>
            ))}
          </nav>
        </div>
        <div className="mgrid dark">
          <span className="half-l px-cell py-5 t-label text-background-400">© {new Date().getFullYear()} RADIUS. All rights reserved.</span>
          <a href="#top" className="half-r px-cell py-5 t-label text-background-400 hover:text-accent-400 cursor-pointer">Back to top ↑</a>
        </div>
      </footer>
    </>
  );
}